import { Box, Grid, Tooltip,Typography } from "@mui/material";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  Cell
} from "recharts";
import React, { useMemo } from "react";

// colours for parties
const PARTY_COLOURS = {
  nat: "#0057B8",
  coalition: "#13265C",
  lab: "#E31C23",
  act: "#FFD100",
  nzf: "#000000",
  green: "#2E8B57",
  tpm: "#CC0033",
  oth: "#a9a19e"
};

// colours for issues
// colours for issues
const ISSUE_COLOURS = {
  inflation: "#a9a19e",
  health: "#e3779c",
  economy: "#88ccee",
  crime: "#9467bd",
  housing: "#c49c94",
  poverty: "#d62728",
  unemployment: "#b59f3b",
  climate: "#2ca02c",
  education: "#1f77b4",
  transport: "#ff7f0e",
  drugs: "#bcbd22",
  race_relations: "#17becf",
  tax: "#ff9896",
  pollution: "#7f7f7f",
  maori: "#8c564b",
  immigration: "#bcbd22",
  defence: "#1f77b4",
};

// colours for issues
const ISSUE_NAMES = {
  unemployment: "Unemployment",
  inflation: "Inflation",
  economy: "Economy",
  health: "Health",
  housing: "Housing",
  crime: "Crime",
  climate:"Climate",
  econ:"Economy",
  drugs:"Drugs",
  defence:"Defence",
  education:"Education",
  maori:"Maori",
  pollution:"Pollution",
  poverty:"Poverty",
  race_rel:"Race relations",
  tax:"Tax",
  transport:"Transport"
  // add the rest
};
const PARTY_NAME = {
    act:"ACT",
  green:"Green",
  lab:"Labour",
  nat:"National",
  nzf:"NZF",
  tpm:"TPM",
  coalition:"Coalition",
  oth:"Other",
}


const NICE_NAME = {
  F:"Female",
  M:"Male",
  Young:"Young",
  Middle:"Middle",
  Old:"Old",
  'Asian only':'Asian only',
  'European only':'European only',
  'European/Māori':'European/Māori',
  'Māori only':'Māori only',
  'Other':'Other',
  'Pacific Peoples only':'Pacific Peoples only',
  ACT:"ACT 2023",
  GREEN:"Green 2023",
  LAB:"Labour 2023",
  NAT:"National 2023",
  NZF:"NZF 2023",
  TPM:"TPM 2023",
  OTH:"Other 2023",
  DNV:"Did not vote",
}
// Helper to add small vertical offsets to avoid overlap
function addRepelOffsets(data, ySpacing = 15, minXDiff = 3) {
  // Group by category
  const grouped = data.reduce((acc, d) => {
    (acc[d.y] = acc[d.y] || []).push(d);
    return acc;
  }, {});

  const result = [];

  Object.values(grouped).forEach(points => {
    // Sort by x so we know left/right order
    points.sort((a, b) => a.x - b.x);

    // Track offsets already applied to avoid compounding too much
    const offsets = new Map();

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      let offset = offsets.get(p) || 0;

      for (let j = 0; j < i; j++) {
        const q = points[j];
        const dx = p.x - q.x;

        if (Math.abs(dx) < minXDiff) {
          // If p is to the right (higher x), push p up, q down
          const dir = dx >= 0 ? 1 : -1;
          offset += dir * ySpacing / 2;
          const prev = offsets.get(q) || 0;
          offsets.set(q, prev - dir * ySpacing / 2);
        }
      }

      offsets.set(p, offset);
    }

    // Merge offsets into result
    points.forEach(p => {
      result.push({ ...p, yOffset: offsets.get(p) || 0 });
    });
  });

  return result;
}


export default function DemogVoteAndIssues({ scatterDemogsRows, chosenDemog }) {
  // ✅ Always call hooks unconditionally
  const rows = scatterDemogsRows?.filter(r => r.type === chosenDemog) || [];
  const niceOrder = Object.keys(NICE_NAME);
  const sortedRows = [...rows].sort(
    (a, b) => niceOrder.indexOf(a.type_code) - niceOrder.indexOf(b.type_code)
  );

  const partyKeys = ["nat", "lab", "act", "green", "tpm", "nzf", "coalition", "oth"];

  // ✅ Build vote-intention data
  const voteData = sortedRows.flatMap(r =>
    partyKeys.map(party => ({
      y: NICE_NAME[r.type_name],
      x: r[`${party}_vi`] * 100,
      party,
    }))
  );
  const legendPayload = Object.keys(PARTY_NAME).map(party => ({
  value: PARTY_NAME[party],      // label in legend
  type: "circle",                // marker shape
  color: PARTY_COLOURS[party],   // marker colour
  id: party,
}));
console.log("legendPayload",)

  // ✅ Compute repelled offsets once per dataset
  const voteDataWithOffsets = useMemo(() => addRepelOffsets(voteData), [voteData]);

  // ✅ Compute demographic order (depends on voteData)
  const demogOrder = useMemo(() => Array.from(new Set(voteData.map(d => d.y))), [voteData]);

  // ✅ Build issue data (safe even if empty)
  const issueData = useMemo(() => {
    if (!scatterDemogsRows?.length) return [];

    const issueKeys = Object.keys(scatterDemogsRows[0]).filter(k => k.endsWith("_issue_n"));

    return scatterDemogsRows
      .filter(r => demogOrder.includes(NICE_NAME[r.type_name]))
      .map(r => {
        const sortedIssues = issueKeys
          .map(k => ({ key: k.replace("_issue_n", ""), val: r[k] }))
          .sort((a, b) => b.val - a.val)
          .slice(0, 3);

        return { demog: r.type_name, issues: sortedIssues };
      });
  }, [scatterDemogsRows, demogOrder]);

  if (!voteDataWithOffsets.length) return null;

  return (
    <Grid container sx={{ height: "100%", width: "100vw" }}>
      <Grid item xs={6} sx={{ height: "100%", width:"50%" }}>
        
      <ResponsiveContainer 
      width="100%"
       height= "100%">
        <ScatterChart layout="vertical">
          <CartesianGrid />
          <XAxis type="number" dataKey="x" domain={[0, 70]} tickFormatter={(v) => v.toFixed(0) + "%"} />
          <YAxis
            type="category"
            dataKey="y"
            allowDuplicatedCategory={false}   // ✅ key fix
            width={100}
          />
          <ReTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: "#fff", border: "1px solid #ccc", padding: "8px" }}>
                    {chosenDemog}: <strong>{d.y}</strong><br />
                    {PARTY_NAME[d.party]}: <strong>{d.x.toFixed(1)}</strong>%
                  </div>
                );
              }}
            />
                       
 
         <Scatter
            data={voteDataWithOffsets}
            shape={(props) => {
              const { cx, cy, fill, payload } = props;
              const offset = payload.yOffset || 0;

              return (
                <g transform={`translate(0, ${offset})`}>
                  <circle cx={cx} cy={cy} r={12} fill={fill} fillOpacity={0.6} />
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="white"
                    fontSize={9}
                    fontWeight="bold"
                  >
                    {payload.x.toFixed(0)}%
                  </text>
                </g>
              );
            }}
          >
            {voteDataWithOffsets.map((d, i) => (
              <Cell key={i} fill={PARTY_COLOURS[d.party]} />
            ))}
          </Scatter>




        </ScatterChart>
      </ResponsiveContainer>
  </Grid>
     <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "95%",
              width: "50%",
              alignItems: "stretch", // rows span width
            }}
          >
            <Box
              sx={{
                height:"100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center", // center vertically
                flex: 1,
                gap: 2,
              }}
            >
              {demogOrder.map((demog) => {
                const row = issueData.find((r) => NICE_NAME[r.demog] === demog);
                return (
                  <Box key={demog} sx={{ height:"100%", display: "flex", gap: 0.5 }}>
                    {row?.issues.map((iss, i) => (
                      <Tooltip
                            bgcolor="white"
                            title={
                              <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {ISSUE_NAMES[iss.key]}, {(iss.val * 100).toFixed(0)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  What is the top issue in New Zealand?
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {chosenDemog}: { demog}  {/* or NICE_NAME[demog] if needed */}
                                </Typography>
                              </Box>
                            }
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "white",
                                  color: "black",
                                  boxShadow: 3,
                                  border: "1px solid #ddd",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: "white", // arrow matches tooltip background
                                },
                              },
                            }}
                          >
                        <Box
                          sx={{
                            
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: ISSUE_COLOURS[iss.key] || "grey",
                            color: "white",
                            fontWeight: "bold",
                            borderRadius: 0,
                            fontSize:20
                          }}
                        >
                          {ISSUE_NAMES[iss.key]}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                );
              })}
            </Box>

      </Grid>
      </Grid>
  );
}
  // {/* RIGHT: top issues */}
  //     <Grid item xs={6}>
  //       <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
  //         {issueData.map(r => (
  //           <Box
  //             key={r.demog}
  //             sx={{ display: "flex", alignItems: "center", gap: 1 }}
  //           >
  //             <Box sx={{ width: 120 }}>{r.demog}</Box>
  //             <Box sx={{ display: "flex", flex: 1 }}>
  //               {r.issues.map((iss, i) => (
  //                 <Tooltip key={iss.key} title={`${iss.key}: ${(iss.val*100).toFixed(1)}%`}>
  //                   <Box
  //                     sx={{
  //                       flex: 1,
  //                       bgcolor: ISSUE_COLOURS[iss.key] || "grey",
  //                       color: "white",
  //                       p: 1,
  //                       textAlign: "center",
  //                     }}
  //                   >
  //                     {iss.key}
  //                   </Box>
  //                 </Tooltip>
  //               ))}
  //             </Box>
  //           </Box>
  //         ))}
  //       </Box>
  //     </Grid>